/**
 * @fileoverview Splide を用いたスライダー制御集約
 * @description KV / NEWS / MOVIE / CHARACTER / MECHANICAL セクションの Splide 初期化と破棄をまとめて管理する。ブレークポイントは window.APP_CONFIG を参照する。
 * @limitations 対象となる DOM が存在しない場合は何も行わずに終了する。Splide 依存のため、ライブラリが読み込まれていない環境では動作しない。
 */

/** レスポンシブ判定に使用するブレークポイント定数 */
const { BREAKPOINT } = window.APP_CONFIG;

import Splide from '@splidejs/splide';

/**
 * 関数の実行を遅延させるdebounce
 * @param {Function} func - 実行する関数
 * @param {number} wait - 待機時間（ミリ秒）
 * @returns {Function} debounceされた関数
 */
const debounce = (func, wait) => {
	let timeout;
	return function executedFunction(...args) {
		const later = () => {
			clearTimeout(timeout);
			func(...args);
		};
		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
	};
};

/* =========================
	KV
========================= */
/**
 * KV セクションの Splide スライダーを初期化する。
 * @returns {void}
 */
export const initKvSlider = () => {
	const el = document.getElementById('js-kv-slider');
	if (!el) return;

	const splide = new Splide(el, {
		type: 'fade',
		autoplay: true,
		loop: false,
		rewind: true,
		interval: 4000,
		speed: 3000,
		arrows: true,
		lazyLoad: 'nearby',
		pagination: false,
		breakpoints: {
			[BREAKPOINT.TAB]: {
				loop: false,
				rewind: true,
			}
		}
	});

	const prevBtn = document.querySelector('.js-kv-prev');
	const nextBtn = document.querySelector('.js-kv-next');

	// 現在のスライドに応じてボタンの状態を更新
	const updateButtonState = () => {
		const index = splide.index;
		// index 0: 最初のスライド → prevボタンを無効化
		// index 1: 最後のスライド → nextボタンを無効化
		if (index === 0) {
			prevBtn?.classList.add('is-inactive');
			nextBtn?.classList.remove('is-inactive');
		} else {
			prevBtn?.classList.remove('is-inactive');
			nextBtn?.classList.add('is-inactive');
		}
	};

	splide.mount();

	// 初期状態とスライド移動時に状態を更新
	splide.on('move', updateButtonState);
	updateButtonState();

	// Splide mounted 完了を他モジュールへ通知（将来の外部連携用）
	// ※ 画像ロード前に発火するため、高さ依存の計算には使用しないこと
	splide.on('mounted', () => {
		el.dispatchEvent(new CustomEvent('kvSliderMounted'));
	});
};

/* =========================
	NEWS
========================= */
/**
 * NEWS セクションの Splide スライダーを初期化する。
 * @returns {void}
 */
export const initNewsSlider = () => {
	const el = document.getElementById('js-news-slider');
	if (!el) return;

	/**
	 * ウィンドウ幅に応じて左パディングを算出する。
	 * @returns {string} パディング値（px または %）
	 */
	const getLeftPadding = () => {
		const base = 1360;
		return window.innerWidth <= base
			? '5%'
			: `${(window.innerWidth - base) / 2}px`;
	};

	const splide = new Splide(el, {
		type: 'slide',
		perPage: 3.5,
		perMove: 1,
		focus: 0,
		gap: '3em',
		arrows: false,
		//omitEnd: true,
		pagination: false,
		updateOnMove: true,
		padding: {
			left: getLeftPadding(),
			right: '0'
		},
		breakpoints: {
			[BREAKPOINT.TAB]: {
				perPage: 2.3,
				gap: '1em',
				padding: {
					left: '5%',
					right: '10%'
				}
			},
			[BREAKPOINT.SP]: {
				perPage: 1.4,
				gap: '1em',
				padding: {
					left: '4%',
					right: '10%'
				}
			}
		}
	});

	splide.mount();

	const prev = document.querySelector('.js-news-prev');
	const next = document.querySelector('.js-news-next');

	prev?.addEventListener('click', () => splide.go('<'));
	next?.addEventListener('click', () => splide.go('>'));

	/**
	 * スライド位置に応じて矢印の活性状態を更新する。
	 * @returns {void}
	 */
	const updateDisabled = () => {
		const last = splide.length - 1;
		prev?.classList.toggle('is-disabled', splide.index === 0);
		next?.classList.toggle('is-disabled', splide.index === last);
	};

	splide.on('mounted moved', updateDisabled);

	window.addEventListener('resize', debounce(() => {
		// PC幅の時だけpaddingを動的に更新（breakpointsの範囲外）
		const isPcWidth = window.innerWidth > BREAKPOINT.TAB;
		if (isPcWidth) {
			splide.options = {
				...splide.options,
				padding: { left: getLeftPadding(), right: '0' }
			};
		}
		splide.refresh();
	}, 200));
};

/* =========================
	GOODS（トップ：NEWSスライダーと同一設定）
========================= */
/**
 * GOODS セクションの Splide スライダーを初期化する。
 * @returns {void}
 */
export const initGoodsSlider = () => {
	const el = document.getElementById('js-goods-slider');
	if (!el) return;

	const getLeftPadding = () => {
		const base = 1360;
		return window.innerWidth <= base
			? '5%'
			: `${(window.innerWidth - base) / 2}px`;
	};

	const splide = new Splide(el, {
		type: 'slide',
		perPage: 4.5,
		perMove: 1,
		focus: 0,
		gap: '3em',
		arrows: false,
		pagination: false,
		updateOnMove: true,
		padding: {
			left: getLeftPadding(),
			right: '0'
		},
		breakpoints: {
			[BREAKPOINT.TAB]: {
				perPage: 3,
				gap: '1em',
				padding: {
					left: '5%',
					right: '10%'
				}
			},
			[BREAKPOINT.SP]: {
				perPage: 2,
				gap: '1em',
				padding: {
					left: '4%',
					right: '10%'
				}
			}
		}
	});

	splide.mount();

	const prev = document.querySelector('.js-goods-prev');
	const next = document.querySelector('.js-goods-next');

	prev?.addEventListener('click', () => splide.go('<'));
	next?.addEventListener('click', () => splide.go('>'));

	const updateDisabled = () => {
		const last = splide.length - 1;
		prev?.classList.toggle('is-disabled', splide.index === 0);
		next?.classList.toggle('is-disabled', splide.index === last);
	};

	splide.on('mounted moved', updateDisabled);

	window.addEventListener('resize', debounce(() => {
		const isPcWidth = window.innerWidth > BREAKPOINT.TAB;
		if (isPcWidth) {
			splide.options = {
				...splide.options,
				padding: { left: getLeftPadding(), right: '0' }
			};
		}
		splide.refresh();
	}, 200));
};

// =============================================================================
// PAST KEY VISUALS 一時廃止（将来復活の可能性あり）
// 復活時は以下のコメントアウトを解除し、BOOT セクションの呼び出しも戻すこと。
// 関連: functions.php（CPT登録）、front-page.php（if(false)ブロック）
// =============================================================================

/* PAST KEY VISUALS 一時廃止
// =========================
// 	PAST KEY VISUALS
// =========================
//
// PAST KEY VISUALS セクションの Splide スライダーを初期化する。
// NEWS スライダーと同一の設定・挙動を適用する。
// @returns {void}
//
// export const initPastKeyVisualsSlider = () => {
// 	const el = document.getElementById('js-past-key-visuals-slider');
// 	if (!el) return;
//
// 	const getLeftPadding = () => {
// 		const base = 1360;
// 		return window.innerWidth <= base
// 			? '5%'
// 			: `${(window.innerWidth - base) / 2}px`;
// 	};
//
// 	const splide = new Splide(el, {
// 		type: 'slide',
// 		perPage: 3.5,
// 		perMove: 1,
// 		focus: 0,
// 		gap: '3em',
// 		arrows: false,
// 		pagination: false,
// 		updateOnMove: true,
// 		padding: {
// 			left: getLeftPadding(),
// 			right: '0'
// 		},
// 		breakpoints: {
// 			[BREAKPOINT.TAB]: {
// 				perPage: 2.3,
// 				gap: '1em',
// 				padding: {
// 					left: '5%',
// 					right: '10%'
// 				}
// 			},
// 			[BREAKPOINT.SP]: {
// 				perPage: 1.4,
// 				gap: '1em',
// 				padding: {
// 					left: '4%',
// 					right: '10%'
// 				}
// 			}
// 		}
// 	});
//
// 	splide.mount();
//
// 	const prev = document.querySelector('.js-pkv-prev');
// 	const next = document.querySelector('.js-pkv-next');
//
// 	prev?.addEventListener('click', () => splide.go('<'));
// 	next?.addEventListener('click', () => splide.go('>'));
//
// 	const updateDisabled = () => {
// 		const last = splide.length - 1;
// 		prev?.classList.toggle('is-disabled', splide.index === 0);
// 		next?.classList.toggle('is-disabled', splide.index === last);
// 	};
//
// 	splide.on('mounted moved', updateDisabled);
//
// 	window.addEventListener('resize', debounce(() => {
// 		const isPcWidth = window.innerWidth > BREAKPOINT.TAB;
// 		if (isPcWidth) {
// 			splide.options = {
// 				...splide.options,
// 				padding: { left: getLeftPadding(), right: '0' }
// 			};
// 		}
// 		splide.refresh();
// 	}, 200));
// };
*/


/* =========================
	MOVIE
========================= */
/**
 * MOVIE セクションのメインビデオ用 Splide スライダーを初期化する。
 * @returns {void}
 */
export const initMovieSlider = () => {
	const el = document.getElementById('js-movie-main');
	if (!el) return;

	const splide = new Splide(el, {
		type: 'fade',
		arrows: false,
		pagination: false,
		lazyLoad: 'nearby',
		speed: 2000,
		updateOnMove: true,
		start: 4, // 0始まりの5番目 = 「FILE 2 予告 60秒」 を初期表示
	});

	splide.mount();

	const thumbs = document.querySelectorAll('.js-movie-thumb');
	let current = null;

	if (thumbs.length) {
		current = thumbs[4]; // start と合わせる
		current.classList.add('is-active');
	}

	thumbs.forEach((thumb, i) => {
		thumb.addEventListener('click', () => splide.go(i));
	});

	splide.on('move', () => {
		current?.classList.remove('is-active');
		current = thumbs[splide.index];
		current?.classList.add('is-active');
	});
};

/* =========================
	CHARACTER FACE
========================= */
/** キャラクター顔スライダーのインスタンス（PC 幅のみ活性） */
let characterSplide = null;

/**
 * Splide を使わない幅帯で、顔リストを確実に表示させる。
 * @param {Element} faceRoot - スライダー根要素（.js-character-face）
 * @returns {void}
 */
const showCharacterFacesAsGrid = (faceRoot) => {
	if (!faceRoot) return;
	faceRoot.classList.add('is-rendered');
	faceRoot.style.visibility = 'visible';
};

/**
 * キャラクター顔スライダーを PC 幅のみで初期化する。
 * @param {Element} article - 対象のキャラクター記事要素
 * @returns {void}
 */
export const mountCharacterSlider = (article) => {
	const el = article.querySelector('.js-character-face');
	/**
	 * CSS のブレークポイント（min-width: 1024px）と同じ条件で PC 判定する。
	 * @type {boolean}
	 */
	const isPcWidth = window.matchMedia
		? window.matchMedia(`(min-width: ${BREAKPOINT.TAB}px)`).matches
		: window.innerWidth >= BREAKPOINT.TAB;

	if (!isPcWidth) {
		destroyCharacterSlider();
		showCharacterFacesAsGrid(el);
		return;
	}

	if (!el) return;

	el.classList.remove('is-rendered');
	el.style.removeProperty('visibility');

	if (characterSplide) return;

	characterSplide = new Splide(el, {
		type: 'slide',
		perPage: 2,
		gap: '3em',
		padding: '2em',
		arrows: true,
		pagination: false,
		speed: 1000,
	});

	characterSplide.mount();

	const prev = article.querySelector('.js-character-prev');
	const next = article.querySelector('.js-character-next');

	prev?.addEventListener('click', () => characterSplide.go('<'));
	next?.addEventListener('click', () => characterSplide.go('>'));
};

/**
 * キャラクター顔スライダーを破棄する。
 * @returns {void}
 */
export const destroyCharacterSlider = () => {
	characterSplide?.destroy(true);
	characterSplide = null;
};


/* =========================
	MECHANICAL
========================= */
/** メカニカルスライダーのインスタンス */
let mechanicalSplide = null;

/**
 * メカニカルセクションのスライダーを初期化する。
 * @param {Element} article - 対象のメカニカル記事要素
 * @returns {void}
 */
export const mountMechanicalSlider = (article) => {
	const el = article.querySelector('.js-mechanical-slider');
	if (!el) return;

	mechanicalSplide = new Splide(el, {
		type: 'fade',
		speed: 2000,
		arrows: true,
		pagination: false,
	});

	mechanicalSplide.mount();
};

/**
 * メカニカルセクションのスライダーを破棄する。
 * @returns {void}
 */
export const destroyMechanicalSlider = () => {
	mechanicalSplide?.destroy(true);
	mechanicalSplide = null;
};


/* =========================
	GOODS GALLERY
========================= */
/** グッズ詳細ギャラリースライダーのインスタンス */
let goodsGallerySplide = null;

/**
 * グッズ詳細ページのギャラリースライダーを初期化する（MECHANICAL と同様の fade）。
 * @returns {void}
 */
export const initGoodsGallerySlider = () => {
	const el = document.getElementById('js-goods-gallery-slider');
	if (!el) return;

	const slides = el.querySelectorAll('.splide__slide');
	if (slides.length < 2) return;

	goodsGallerySplide = new Splide(el, {
		type: 'fade',
		speed: 2000,
		arrows: true,
		pagination: false,
	});

	goodsGallerySplide.mount();
};

/**
 * グッズ詳細ページのギャラリースライダーを破棄する。
 * @returns {void}
 */
export const destroyGoodsGallerySlider = () => {
	goodsGallerySplide?.destroy(true);
	goodsGallerySplide = null;
};


/* =========================
	BOOT
========================= */
/**
 * DOMContentLoaded 後に主要セクションのスライダーを初期化する。
 * @returns {void}
 */
document.addEventListener('DOMContentLoaded', () => {
	initKvSlider();
	initNewsSlider();
	initGoodsSlider();
	// initPastKeyVisualsSlider(); // PAST KEY VISUALS 一時廃止
	initMovieSlider();
	initGoodsGallerySlider();
});
